import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ESG_ICONS } from '../../constants/esgIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { CarbonAvoidedByMaterial } from '../../types/esg-dashboard.type';
import { carbonMaterialLabel } from '../../utils/carbonAvoidance.util';
import ChartPanel from '../charts/ChartPanel';
import EmptyState from '../EmptyState';

interface CarbonAvoidedByMaterialTableProps {
  rows: CarbonAvoidedByMaterial[];
  title?: string;
  fillHeight?: boolean;
  caption?: React.ReactNode;
  id?: string;
}

const efCaptionSx = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.75rem', color: 'text.secondary' };

/** Avoided emissions by material category → ordered material type, with EF version per line. */
const CarbonAvoidedByMaterialTable: React.FC<CarbonAvoidedByMaterialTableProps> = ({
  rows,
  title,
  fillHeight = false,
  caption,
  id,
}) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const resolvedTitle = title ?? t('metrics.carbon_avoidance_material');
  const tco2e = (v: number) => fmt.metric('tco2e', v).text;

  return (
    <ChartPanel
      id={id}
      title={resolvedTitle}
      icon={ESG_ICONS.materials}
      caption={caption ?? t('units.tco2e')}
      sx={fillHeight ? { height: '100%' } : undefined}
    >
      {!rows.length ? (
        <EmptyState title={t('dashboard.alerts.noCarbonData')} variant="inline" minHeight={140} />
      ) : (
        <TableContainer sx={{ mx: { xs: -2, md: -2.5 }, width: 'auto', overflowX: 'auto' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 420 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ paddingInlineStart: { xs: 2, md: 2.5 } }}>{t('carbonAvoidance.material')}</TableCell>
                <TableCell>{t('carbonAvoidance.stream')}</TableCell>
                <TableCell align="right">{t('carbonAvoidance.avoidedTco2e')}</TableCell>
                <TableCell sx={{ paddingInlineEnd: { xs: 2, md: 2.5 } }}>{t('carbonAvoidance.efVersion')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((group) => (
                <React.Fragment key={group.category_label}>
                  <TableRow sx={{ bgcolor: 'background.elevated' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 600, paddingInlineStart: { xs: 2, md: 2.5 } }}>
                      {carbonMaterialLabel(group.category_label, t('carbonAvoidance.unclassified'))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <bdi>{tco2e(group.total_avoided_tco2e)}</bdi>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                  {group.materials.map((line, idx) => (
                    <TableRow key={`${group.category_label}-${line.material_type}-${idx}`} hover>
                      <TableCell sx={{ paddingInlineStart: { xs: 4, md: 4.5 } }}>{line.material_type}</TableCell>
                      <TableCell>
                        {carbonMaterialLabel(line.material_label, t('carbonAvoidance.unclassified'))}
                      </TableCell>
                      <TableCell align="right">
                        <bdi>{tco2e(line.avoided_tco2e)}</bdi>
                      </TableCell>
                      <TableCell sx={{ paddingInlineEnd: { xs: 2, md: 2.5 } }}>
                        <Box component="span" sx={efCaptionSx}>
                          {line.emission_factor_version_label}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ChartPanel>
  );
};

export default CarbonAvoidedByMaterialTable;
